// Identity of the data controller and the effective date of the legal documents.
//
// Everything a lawyer or an accountant needs to change lives HERE, in one object, and is
// interpolated into every translation of the privacy policy. The five content files under
// `@/lib/legal/` must never hardcode a company name, a VAT number or an address.
//
// Every field is optional except `legalName` and `email`: an empty string drops the row from
// the identity table AND the clause from the prose, in all five languages. That is what lets
// the policy omit the registered address of a sole trader, which for a ditta individuale is
// usually a home address.
//
// ⚠️ What omitting them does NOT do: art. 7 of D.lgs. 70/2003 obliges anyone selling online in
// Italy to publish, accessibly and permanently, their name, domicile or registered office and
// VAT number. That duty is e-commerce law, not GDPR, and it is discharged by a legal notice
// somewhere on the site — not necessarily inside this document. It is still open. See the
// "Adempimenti aperti" note in docs/PRIVACY-RUNBOOK.md.
//
// `hasPlaceholders()` is what the page uses to shout, in development only, about any « » left.

export const CONTROLLER = {
  // Ditta individuale: the legal name is the holder's own name, optionally followed by the
  // trade name ("ditta individuale" / "impresa individuale").
  legalName: "Giuseppe Buomprisco",
  tradeName: "VisioScreening",
  vat: "",                                   // Partita IVA — omitted here, see the note above
  taxCode: "",                               // Codice fiscale
  reaNumber: "",                             // Optional: "REA «Provincia»-«numero»"
  address: "",                               // Omitted: sole trader's address is a home address
  city: "",
  country: "Italia",
  // The address that already answers on the support line, so a rights request cannot land in
  // a mailbox nobody watches — the failure mode of a dedicated privacy@ alias that was never
  // created. Kept in step with SUPPORT_EMAIL in @/components/steps/Intro.
  email: "assistenza@medcompetente.it",
  pec: "",                                   // Optional
  phone: "",                                 // Optional — leave "" to hide the row
};

/** Effective date of the current version of the policy (ISO, no time). */
export const PRIVACY_EFFECTIVE_DATE = "2026-08-10";

/** Bumped whenever the substance of the policy changes, not on typo fixes. */
export const PRIVACY_VERSION = "1.0";

/**
 * The Italian text is the authoritative one: the controller is established in Italy and any
 * dispute is decided under Italian law, so a translation can only ever be a courtesy copy.
 */
export const AUTHORITATIVE_LANG = "it";

const PLACEHOLDER = /[«»]/;

/** Fields still holding a « » placeholder. Empty array once the identity is filled in. */
export function placeholderFields() {
  return Object.entries(CONTROLLER)
    .filter(([, v]) => typeof v === "string" && PLACEHOLDER.test(v))
    .map(([k]) => k);
}

export function hasPlaceholders() {
  return placeholderFields().length > 0;
}

/** "Nome Cognome — VisioScreening", or just the legal name when there is no trade name. */
export function controllerDisplayName() {
  return CONTROLLER.tradeName
    ? `${CONTROLLER.legalName} — ${CONTROLLER.tradeName}`
    : CONTROLLER.legalName;
}

/**
 * Registered office on one line, for prose that cannot afford a list — or "" when no address
 * is published. Callers must treat "" as "omit the whole clause", never as an empty slot:
 * "con sede in ." reads as a bug and undermines the document it appears in.
 */
export function controllerAddressLine() {
  return [CONTROLLER.address, CONTROLLER.city, CONTROLLER.country].filter(Boolean).join(", ");
}

export function hasPostalAddress() {
  return Boolean(CONTROLLER.address || CONTROLLER.city);
}

/** Contact routes actually published, in the order a reader should try them. */
export function contactRoutes() {
  return [CONTROLLER.email, CONTROLLER.pec, CONTROLLER.phone].filter(Boolean);
}

export function formatLegalDate(iso, locale) {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });
}
